/**
 * My day -- the provider's own planned visits for one calendar day (ruling,
 * Isaiah Cotto, 2026-10-19: providers see where they are going and what to do
 * there, from the visit plans Scheduling keeps in eMerge).
 *
 * One read for the plan (`GET /emerge/day-sheet/my-day`), then, behind the same
 * login, two day-sheet reads per building for the residents: the current
 * manifest and the approved-to-dispense (ATD) lens. Each building answers on
 * its own, so one refused or failed manifest never takes the page down.
 *
 * Non-data answers each get their own words:
 *   linked:false             this login has no provider record yet (an explanation)
 *   403 DENY_NO_CAPABILITY   no building is assigned to this login yet (an explanation)
 *   404 NOT_FOUND            the building in the address is not one of theirs
 *   400 DATE_INVALID         the date in the address is not a calendar day
 *   failure                  the server was not reached or failed: Try again
 *
 * Visits are shown in the server's order (route order, then arrival time, then
 * name) and never re-sorted. Arrival times are America/New_York wall-clock
 * 'HH:MM', formatted as text and never converted by the browser's zone; this
 * screen never invents a time or a stop. Read-only: Scheduling plans visits. No resident name or id is
 * ever put in a URL, and only the few manifest columns named below are drawn.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpRight, CalendarDays, ChevronLeft, ChevronRight, MapPin, Sun } from "lucide-react";
import { Button } from "../../../components/shadcn/ui/button";
import { Card } from "../../../components/shadcn/ui/card";
import { StateBlock as TideState } from "../../../components/tide";
import { roundProgressWords } from "../roundProgress";
import {
  TESTING_STATUS_LABEL,
  closureSentence,
  closureWords,
  patientName,
  providerApi,
  relativeDay,
  shortDate,
} from "../providerApi";
import type { DaySheet, MyDay, MyDayVisit } from "../providerApi";
import { Notice, Section, StatusBadge } from "./FieldPrimitives";
import type { BadgeTone } from "./FieldPrimitives";
import { RoomEditedMark } from "./RoomField";
import "./field-workspace.css";

type Outcome =
  | { kind: "ok"; data: MyDay }
  | { kind: "unassigned" }
  | { kind: "notfound" }
  | { kind: "baddate" }
  | { kind: "failure"; status?: number; message: string };

type Residents =
  | { kind: "loading" }
  | { kind: "ok"; manifest: DaySheet; atd: DaySheet | null }
  | { kind: "refused" }
  | { kind: "failure" };

const STATE_LABEL: Record<MyDayVisit["state"], string> = {
  PLANNED: "Planned",
  CONFIRMED: "Confirmed",
  COMPLETED: "Completed",
};
const STATE_TONE: Record<MyDayVisit["state"], BadgeTone> = {
  PLANNED: "amber",
  CONFIRMED: "green",
  COMPLETED: "indigo",
};

const APPLE_PREFIX = "https://maps.apple.com/?q=";
const GOOGLE_PREFIX = "https://www.google.com/maps/search/?api=1&query=";

const bare = (code: string | undefined) => String(code || "").replace(/^AUTHZ_/, "");
const list = <T,>(v: T[] | undefined | null): T[] => (Array.isArray(v) ? v : []);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const SUBTITLE = "The buildings you are planned to visit, with where to go and what to do there.";

/** 'HH:MM' (24-hour, New York local) -> '9:30 AM'. Text only: no Date, so the browser's zone cannot shift it. */
export function arrivalWords(hhmm: string | null | undefined): string | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(hhmm || ""));
  if (!m) return null;
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
}

function MapLinks({ maps }: { maps: MyDayVisit["facility"]["maps"] }) {
  // Defense in depth: the server builds these, but a link is only drawn when it is a maps search and nothing else.
  const apple = maps && typeof maps.apple === "string" && maps.apple.startsWith(APPLE_PREFIX) ? maps.apple : null;
  const google = maps && typeof maps.google === "string" && maps.google.startsWith(GOOGLE_PREFIX) ? maps.google : null;
  if (!apple && !google) return <p className="fw-muted">No map link is available for this building.</p>;
  return (
    <div className="fw-day-maps">
      {apple && (
        <a className="fw-resource" href={apple} target="_blank" rel="noopener noreferrer">
          Open in Apple Maps <ArrowUpRight aria-hidden="true" />
        </a>
      )}
      {google && (
        <a className="fw-resource" href={google} target="_blank" rel="noopener noreferrer">
          Open in Google Maps <ArrowUpRight aria-hidden="true" />
        </a>
      )}
      {maps && maps.from_address === false && (
        <small className="fw-muted">Searched by building name - no street address on file</small>
      )}
    </div>
  );
}

function Instructions({ text }: { text: string | null }) {
  return (
    <div className="fw-day-instructions">
      <h4>{text ? "Instructions from Scheduling" : "Standard instructions"}</h4>
      {text ? (
        <p className="fw-day-prewrap">{text}</p>
      ) : (
        <ul>
          <li>Check in with the facility contact or SSD when you arrive.</li>
          <li>Work from the current manifest below.</li>
          <li>Residents approved to dispense (ATD) are marked.</li>
          <li>To change this plan, ask Scheduling.</li>
        </ul>
      )}
      <small className="fw-muted">Scheduling writes visit instructions when the visit is planned.</small>
    </div>
  );
}

function ResidentsPanel({ state, onRetry }: { state: Residents | undefined; onRetry: () => void }) {
  if (!state || state.kind === "loading") {
    return <TideState kind="loading" shape="lines" label="Loading the manifest…" busy={false} />;
  }
  if (state.kind === "refused") {
    return <p className="fw-muted" role="status">Your access does not open this building&rsquo;s manifest.</p>;
  }
  if (state.kind === "failure") {
    return (
      <div className="fw-day-fail" role="status">
        <p className="fw-muted">The manifest did not load.</p>
        <Button variant="outline" onClick={onRetry}>Try again</Button>
      </div>
    );
  }
  const { manifest, atd } = state;
  const rows = list(manifest.roster?.rows);
  const atdRows = list(atd?.roster?.rows);
  const atdIds = new Set(atdRows.map((r) => r.id));
  const manifestIds = new Set(rows.map((r) => r.id));
  const atdOnly = atdRows.filter((r) => !manifestIds.has(r.id));
  const total = manifest.roster?.total ?? rows.length;
  const contacts = list(manifest.contacts?.all);
  return (
    <div className="fw-day-residents">
      <div className="fw-day-counts">
        <span>Current manifest <strong>{manifest.roster?.lenses?.manifest ?? "—"}</strong></span>
        <span>ATD approved <strong>{atd ? (atd.roster?.lenses?.atd ?? manifest.roster?.lenses?.atd ?? "—") : (manifest.roster?.lenses?.atd ?? "—")}</strong></span>
      </div>
      {rows.length ? (
        <ul className="fw-day-rows" aria-label="Current manifest">
          {rows.map((r) => (
            <li key={r.id}>
              <strong>{patientName(r)}</strong>
              <span>Room {r.room_number || "not recorded"}</span>
              {r.room_edited && <RoomEditedMark monday={r.room_monday} />}
              <span>{r.status || "Status not recorded"}</span>
              {r.testing_status && TESTING_STATUS_LABEL[r.testing_status] && (
                <span>{TESTING_STATUS_LABEL[r.testing_status]}</span>
              )}
              {atdIds.has(r.id) && <StatusBadge tone="green">ATD</StatusBadge>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="fw-muted">No resident is on the current manifest here.</p>
      )}
      {total > rows.length && (
        <p className="fw-muted">
          Showing first {rows.length} of {total}; open the facility for the full manifest.
        </p>
      )}
      {!atd && <p className="fw-muted">The ATD marks did not load, so no resident is marked here.</p>}
      {atd && (atd.roster?.total ?? atdRows.length) > atdRows.length && (
        <p className="fw-muted">
          ATD marks cover the first {atdRows.length} of {atd.roster.total} approved residents.
        </p>
      )}
      {atdOnly.length > 0 && (
        <>
          <h4>Marked ATD</h4>
          <ul className="fw-day-rows" aria-label="Marked ATD, not on the current manifest">
            {atdOnly.map((r) => (
              <li key={r.id}>
                <strong>{patientName(r)}</strong>
                <span>Room {r.room_number || "not recorded"}</span>
                {r.room_edited && <RoomEditedMark monday={r.room_monday} />}
                <StatusBadge tone="green">ATD</StatusBadge>
              </li>
            ))}
          </ul>
        </>
      )}
      <h4>Facility contacts</h4>
      {contacts.length ? (
        <ul className="fw-day-contacts" aria-label="Facility contacts">
          {contacts.map((c) => (
            <li key={c.id}>
              <strong>{c.name || "Name not recorded"}</strong>
              <span>{c.title || "Role not recorded"}</span>
              {c.phone && <a href={`tel:${c.phone}`}>{c.phone}</a>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="fw-muted">No facility contact is recorded.</p>
      )}
    </div>
  );
}

function VisitCard({
  visit,
  focused,
  residents,
  onRetry,
  onOpenFacility,
}: {
  visit: MyDayVisit;
  focused: boolean;
  residents: Residents | undefined;
  onRetry: () => void;
  onOpenFacility: (id: string) => void;
}) {
  const f = visit.facility;
  const name = f.name || "Facility";
  const arrival = arrivalWords(visit.arrival_time);
  const stop = typeof visit.route_order === "number" ? visit.route_order : null;
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Once on load, bring the building from the email into view (jsdom has no scrollIntoView).
    const el = ref.current;
    if (focused && el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
  }, [focused]);
  return (
    <div ref={ref}>
    <Card className="fw-card fw-day-visit" data-focus={focused || undefined} aria-current={focused ? "true" : undefined}
      role="group" aria-label={`Visit at ${name}`}>
      <div className="fw-card-head">
        <div>
          {stop !== null && <small className="fw-day-stop"><strong>Stop {stop}</strong></small>}
          <h3>{name}</h3>
          <p className="fw-day-arrival">{arrival ? `Arrive about ${arrival}` : <span className="fw-muted">No arrival time set</span>}</p>
          {focused && <small className="fw-muted">Opened from your link</small>}
          <p>
            <MapPin aria-hidden="true" className="fw-day-pin" />
            {f.address_line || "No street address recorded"}
          </p>
        </div>
        <div className="fw-sched-line">
          <StatusBadge tone={STATE_TONE[visit.state] || "neutral"}>{STATE_LABEL[visit.state] || visit.state}</StatusBadge>
          {visit.building_closed && <StatusBadge tone="rose">{closureWords(visit.building_closed)}</StatusBadge>}
          {roundProgressWords(visit.round) && (
            <StatusBadge tone={visit.round && visit.round.seen >= visit.round.total ? "green" : "indigo"}>
              <span aria-label={`Today's round: ${roundProgressWords(visit.round)}`}>{roundProgressWords(visit.round)}</span>
            </StatusBadge>
          )}
        </div>
      </div>
      <div className="fw-panel-content fw-day-body">
        <p>{visit.purpose || <span className="fw-muted">No purpose recorded</span>}</p>
        {visit.building_closed && <Notice>{closureSentence(visit.building_closed)}.</Notice>}
        <MapLinks maps={f.maps} />
        <Instructions text={visit.instructions} />
        <div>
          <Button variant="outline" onClick={() => onOpenFacility(f.id)} aria-label={`Open this facility: ${name}`}>
            Open this facility <ArrowUpRight aria-hidden="true" />
          </Button>
        </div>
        <h4>Residents</h4>
        <ResidentsPanel state={residents} onRetry={onRetry} />
      </div>
    </Card>
    </div>
  );
}

export default function FieldMyDay({
  facilityId,
  date,
  onSelectDate,
  onOpenFacility,
}: {
  /** The building in the address, if any: said by the server as `focus`, never a filter on the day. */
  facilityId?: string | null;
  /** The calendar day in the address, or null for the server's default. */
  date: string | null;
  onSelectDate: (date: string | null) => void;
  onOpenFacility: (id: string) => void;
}) {
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [residents, setResidents] = useState<Record<string, Residents>>({});
  const request = useRef(0);
  const generation = useRef(0);
  useEffect(() => () => { request.current += 1; generation.current += 1; }, []);

  const load = useCallback(async () => {
    const mine = ++request.current;
    setOutcome(null);
    try {
      // The day is never narrowed to the building in the address: My day is every building of the day (the server's
      // facility_id would filter it, and 404 a building the schedule scope does not reach). The address's building
      // is only highlighted, client-side.
      const res = await providerApi.getMyDay(date, null);
      if (mine !== request.current) return;
      if (res.success && res.data) setOutcome({ kind: "ok", data: res.data });
      else if (bare(res.code) === "DENY_NO_CAPABILITY") setOutcome({ kind: "unassigned" });
      else if (res.status === 404 || res.code === "NOT_FOUND") setOutcome({ kind: "notfound" });
      else if (res.status === 400 || res.code === "DATE_INVALID") setOutcome({ kind: "baddate" });
      else setOutcome({ kind: "failure", status: res.status, message: res.message || res.error || "Your day did not load." });
    } catch {
      if (mine !== request.current) return;
      setOutcome({ kind: "failure", message: "Could not reach the server, so your day is not shown." });
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const loadResidents = useCallback(async (id: string) => {
    const mine = generation.current;
    setResidents((prev) => ({ ...prev, [id]: { kind: "loading" } }));
    const set = (r: Residents) => { if (mine === generation.current) setResidents((prev) => ({ ...prev, [id]: r })); };
    try {
      const [m, a] = await Promise.all([
        providerApi.getDaySheet(id, { lens: "manifest", limit: 200, order: "room" }),
        providerApi.getDaySheet(id, { lens: "atd", limit: 200, order: "room" }),
      ]);
      if (m.success && m.data) set({ kind: "ok", manifest: m.data, atd: a.success && a.data ? a.data : null });
      else if (m.status === 403 || m.status === 404) set({ kind: "refused" });
      else set({ kind: "failure" });
    } catch {
      set({ kind: "failure" });
    }
  }, []);

  const visits = outcome?.kind === "ok" ? list(outcome.data.selected?.visits) : [];
  const idsKey = Array.from(new Set(visits.map((v) => v.facility.id))).sort().join(",");
  useEffect(() => {
    generation.current += 1;
    setResidents({});
    if (idsKey) idsKey.split(",").forEach((id) => { loadResidents(id); });
  }, [idsKey, loadResidents]);

  if (!outcome) {
    return (
      <Section title="My day" subtitle={SUBTITLE}>
        <div className="fw-panel-content">
          <TideState kind="loading" shape="lines" label="Loading your day…" busy={false} />
        </div>
      </Section>
    );
  }

  if (outcome.kind === "unassigned") {
    return (
      <Section title="My day" subtitle={SUBTITLE}>
        <div className="fw-empty" role="status">
          <CalendarDays aria-hidden="true" />
          <h3>No facility is assigned to you yet</h3>
          <p>
            Your day lists the visits Scheduling has planned for you at the facilities you cover.
            Scheduling records that assignment in eMerge, and your access follows once your login is
            linked to your provider record. If you expected to see a visit here, ask Scheduling to
            check your assignment and your login link.
          </p>
        </div>
      </Section>
    );
  }

  if (outcome.kind === "notfound" || outcome.kind === "baddate") {
    return (
      <Section title="My day" subtitle={SUBTITLE}>
        <div className="fw-empty" role="status">
          <CalendarDays aria-hidden="true" />
          <h3>{outcome.kind === "notfound" ? "This facility is not one of yours" : "That date is not a valid day"}</h3>
          <p>
            {outcome.kind === "notfound"
              ? "The facility in this address is not one of the buildings you are assigned to cover."
              : "The date in this address is not a calendar day this screen can show."}
          </p>
          <Button variant="outline" onClick={() => onSelectDate(null)}>Back to today</Button>
        </div>
      </Section>
    );
  }

  if (outcome.kind === "failure") {
    return (
      <Section title="My day" subtitle={SUBTITLE}>
        <div className="fw-panel-content">
          <TideState kind="error" heading="Your day did not load" headingLevel={3} message={outcome.message}
            status={outcome.status} tone="danger" onRetry={load} />
        </div>
      </Section>
    );
  }

  const { data } = outcome;

  if (!data.linked) {
    return (
      <Section title="My day" subtitle={SUBTITLE}>
        <div className="fw-empty" role="status">
          <Sun aria-hidden="true" />
          <h3>Your login is not linked to a provider record</h3>
          <p>{data.message || "Ask Scheduling to link your login to your provider record."}</p>
        </div>
      </Section>
    );
  }

  const days = list(data.days);
  const selectedDate = data.date ?? data.selected?.date ?? "";
  const prev = [...days].reverse().find((d) => d.date < selectedDate) || null;
  const next = days.find((d) => d.date > selectedDate) || null;
  const outside = data.outside_scope || 0;
  const rel = relativeDay(selectedDate);

  return (
    <div className="fw-sched fw-day">
      <Section
        title="My day"
        subtitle={SUBTITLE}
        action={data.provider?.name ? <StatusBadge tone="neutral">{data.provider.name}</StatusBadge> : undefined}
      >
        <div className="fw-panel-content">
          {days.length ? (
            <div className="fw-day-strip" role="group" aria-label="Days with a planned visit">
              <Button variant="ghost" disabled={!prev} onClick={() => prev && onSelectDate(prev.date)} aria-label="Previous visit day">
                <ChevronLeft aria-hidden="true" />
              </Button>
              <ul>
                {days.map((d) => {
                  const current = d.date === selectedDate;
                  return (
                    <li key={d.date}>
                      <button
                        type="button"
                        className="fw-day-chip"
                        aria-pressed={current}
                        aria-current={current ? "date" : undefined}
                        onClick={() => onSelectDate(d.date)}
                      >
                        <strong>{shortDate(d.date)}</strong>
                        <small>{plural(d.visits, "visit", "visits")} · {plural(d.facilities, "building", "buildings")}</small>
                        {d.closed > 0 && <small className="fw-day-closed">{d.closed} closed</small>}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <Button variant="ghost" disabled={!next} onClick={() => next && onSelectDate(next.date)} aria-label="Next visit day">
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
          ) : (
            <p className="fw-muted">No visit is planned for you in the next {data.window?.days ?? 28} days.</p>
          )}
          {visits.length > 0 && (
            <p className="fw-muted">
              {visits.some((v) => arrivalWords(v.arrival_time) || typeof v.route_order === "number")
                ? "Visits are listed in route order. Arrival times are Eastern Time (ET)."
                : "No arrival time or route order is set for this day yet; buildings are listed by name."}
            </p>
          )}
        </div>
      </Section>

      {outside > 0 && (
        <Notice>
          {outside} of your planned visits {outside === 1 ? "is" : "are"} at buildings your access does not open yet.
          Ask Scheduling to check your coverage.
        </Notice>
      )}

      <h2 className="fw-day-date">
        {shortDate(selectedDate)}
        {rel && <small>{rel}</small>}
      </h2>

      {visits.length ? (
        visits.map((v) => (
          <VisitCard
            key={v.plan_id}
            visit={v}
            focused={v.facility.id === (facilityId || data.focus)}
            residents={residents[v.facility.id]}
            onRetry={() => loadResidents(v.facility.id)}
            onOpenFacility={onOpenFacility}
          />
        ))
      ) : (
        <Section title={`No visit is planned for you on ${shortDate(selectedDate)}`}>
          <div className="fw-panel-content">
            <p className="fw-muted">
              {next ? "Choose another day above to see your next planned visit." : "Scheduling has not planned another visit for you in this window."}
            </p>
          </div>
        </Section>
      )}

      <Notice>
        This view is read-only. Scheduling plans visits in eMerge; ask Scheduling to move or change one.
      </Notice>
    </div>
  );
}
